import {
  AlertTriangle,
  Briefcase,
  Building,
  Calendar,
  CircleCheck,
  CreditCard,
  Home,
  Mail,
  Phone,
  User,
  Wallet,
  X,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent } from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";

const STORAGE_KEY = "lead-alugar-vs-financiar";

interface LeadCaptureFormProps {
  valorImovel: number;
  entrada: number;
  prazo: number;
  scenario: "renting" | "financing";
}

function getStoredLead() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function formatCurrency(value: number): string {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

function validateCPF(cpf: string): boolean {
  const cleanCPF = cpf.replace(/\D/g, "");
  if (cleanCPF.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleanCPF)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let rest = 11 - (sum % 11);
  let digit1 = rest > 9 ? 0 : rest;
  if (digit1 !== parseInt(cleanCPF.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  rest = 11 - (sum % 11);
  let digit2 = rest > 9 ? 0 : rest;
  if (digit2 !== parseInt(cleanCPF.charAt(10))) return false;

  return true;
}

export const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({
  valorImovel: propValorImovel,
  entrada,
  prazo,
  scenario,
}) => {
  const [formData, setFormData] = useState({
    nome: "",
    cpf: "",
    dataNascimento: "",
    valorImovel: propValorImovel,
    entrada: entrada,
    prazo: prazo,
    telefone: "",
    email: "",
    timeToBuy: "",
    tipoImovel: "",
    rendaMensal: "",
    perfilProfissional: "",
    interesseFinanciamento: "",
  });

  const [isAuvpStudent, setIsAuvpStudent] = useState(false);
  const [submitted, setSubmitted] = useState(() => !!getStoredLead());
  const [showLowIncome, setShowLowIncome] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showTestingApp, setShowTestingApp] = useState(false);

  useEffect(() => {
    if (submitted) return;
    const wpData = (window as any).WP_Data;
    if (!wpData) return;
    setFormData((prev) => ({
      ...prev,
      nome: wpData.user_name || prev.nome,
      email: wpData.user_email || prev.email,
    }));
  }, [submitted]);

  const creditValue = formData.valorImovel - formData.entrada;
  const isNotEligible = creditValue <= 100000;
  const isPropertyValueTooLow =
    formData.valorImovel > 0 && formData.valorImovel < 500000;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const errors: string[] = [];
    if (!formData.nome.trim()) errors.push("Nome");
    if (
      !formData.telefone.trim() ||
      formData.telefone.replace(/\D/g, "").length < 10
    )
      errors.push("Telefone válido (WhatsApp)");
    if (
      !formData.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    )
      errors.push("E-mail válido");
    if (!formData.cpf.trim() || !validateCPF(formData.cpf))
      errors.push("CPF válido");
    if (!formData.dataNascimento) errors.push("Data de nascimento");
    if (!formData.tipoImovel) errors.push("Tipo de imóvel");
    if (!formData.timeToBuy) errors.push("Quando quer iniciar o financiamento");
    const rendaNum = parseFloat(formData.rendaMensal);
    if (!formData.rendaMensal || isNaN(rendaNum) || rendaNum <= 0)
      errors.push("Renda mensal");
    if (!formData.perfilProfissional) errors.push("Perfil profissional");
    if (!formData.interesseFinanciamento)
      errors.push("Interesse no financiamento");

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);

    if (formData.interesseFinanciamento === "nao") {
      setShowTestingApp(true);
      return;
    }

    if (parseFloat(formData.rendaMensal) < 15000) {
      setShowLowIncome(true);
      return;
    }

    const trimmedName = formData.nome.trim();
    const leadData = {
      name: isAuvpStudent ? `[AL] ${trimmedName}` : trimmedName,
      email: formData.email.trim(),
      phone: formData.telefone.trim(),
      cpf: formData.cpf.trim(),
      dataNascimento: formData.dataNascimento,
      valorImovel: formData.valorImovel,
      tipoImovel: formData.tipoImovel,
      timeToBuy: formData.timeToBuy,
      rendaMensal: parseFloat(formData.rendaMensal),
      perfilProfissional: formData.perfilProfissional,
      interesseFinanciamento: formData.interesseFinanciamento,
      capturedAt: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(leadData));

    const phoneDigits = leadData.phone.replace(/\D/g, "");
    const cpfDigits = leadData.cpf.replace(/\D/g, "");

    const isLocal = window.location.hostname.includes("localhost");
    const baseUrl = isLocal ? "https://investidorsardinha.r7.com" : "";

    const dealBody = {
      pipelineId: "d4ddbefa-21fd-45be-ab92-bbcdbd012e2e",
      assetSegment: "Realty",
      product: {
        id: "946d5180-42c2-49e5-b8da-ed53dd691b4b",
      },
      downPayment: formData.entrada,
      operationValue: Math.max(0, formData.valorImovel - formData.entrada),
      financedValue: 0,
      deadline: formData.prazo,
      objective: "comprar",
      timeToBuy: formData.timeToBuy,
      realty: {
        value: formData.valorImovel,
        type: formData.tipoImovel,
      },
      associates: [
        {
          main: true,
          notify: true,
          nationality: "brazilian",
          type: 1,
          document: cpfDigits,
          name: leadData.name,
          birthDate: leadData.dataNascimento,
          contacts: [
            { type: "email", value: leadData.email, main: true },
            {
              type: "cellphone",
              value: phoneDigits,
              main: true,
            },
          ],
          incomes: [
            {
              main: true,
              mensalIncome: leadData.rendaMensal,
              professionalProfile: leadData.perfilProfissional,
            },
          ],
        },
      ],
    };

    fetch(`${baseUrl}/wp-json/sardine/v1/deal`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dealBody),
    }).catch(() => {});

    try {
      const tracker = (window as any).pulseTracker;
      if (tracker?.trackEvent) {
        tracker.trackEvent(
          "lead_capture_alugar_vs_financiar",
          leadData,
          leadData.email,
        );
      }
    } catch {}

    setSubmitted(true);
  };

  const handleClose = () => {
    setIsExpanded(false);
  };

  const handleAdjustIncome = () => {
    setShowLowIncome(false);
    setFormData({ ...formData, rendaMensal: "", perfilProfissional: "" });
  };

  const handleReset = () => {
    setSubmitted(false);
    setIsExpanded(true);
    setShowLowIncome(false);
    setShowTestingApp(false);
    setValidationErrors([]);
    setFormData((prev) => ({
      ...prev,
      cpf: "",
      dataNascimento: "",
      valorImovel: propValorImovel,
      entrada: entrada,
      prazo: prazo,
      telefone: "",
      email: "",
      timeToBuy: "",
      tipoImovel: "",
      rendaMensal: "",
      perfilProfissional: "",
      interesseFinanciamento: "",
    }));
    localStorage.removeItem(STORAGE_KEY);
  };

  const headlineText =
    scenario === "financing"
      ? "Vamos tirar seu financiamento do papel?"
      : "Ainda quer comprar um imóvel?";
  const subheadlineText =
    scenario === "financing"
      ? "Fazemos sua análise com taxas atuais de mercado em vários bancos e acompanhamos você até a aprovação."
      : "Podemos estruturar um plano para viabilizar seu crédito com mais eficiência e menor impacto financeiro.";
  const ctaText =
    scenario === "financing"
      ? "QUERO AVANÇAR COM MEU CRÉDITO"
      : "QUERO ANALISAR MEU CRÉDITO";

  return (
    <>
      {/* Banner sticky — sempre visível */}
      <div className="w-full bg-[#f2eeef] rounded-2xl border border-primary px-4 md:px-6 py-4 my-12 sticky top-4 z-30 animate-in fade-in slide-in-from-top-4 duration-500 shadow-md">
        <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-primary text-balance">
              {headlineText}
            </h2>
            <p className="text-base text-muted-foreground mt-1 text-pretty">
              {subheadlineText}
            </p>
          </div>
          <div className="flex flex-col items-center gap-2 w-full sm:w-auto shrink-0">
            <img
              src="https://auvpcapital.com.br/wp-content/uploads/2025/03/AUVP-CAPITAL-HORIZONTAL-AMARELA.svg"
              alt="AUVP Capital"
              className="h-14 object-contain hidden md:block"
            />
            <Button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="w-full sm:w-auto h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 uppercase tracking-wide text-xs whitespace-nowrap"
            >
              {ctaText}
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog com formulário */}
      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent className="max-w-[90%] md:max-w-3xl bg-[#f2eeef] border border-primary rounded-2xl max-h-[90vh] overflow-y-auto">
          {showLowIncome ? (
            <div className="flex flex-col items-center justify-center py-8 animate-in fade-in zoom-in-95 duration-500">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-5">
                <AlertTriangle className="w-9 h-9 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground text-center text-balance">
                {"Renda insuficiente"}
              </h3>
              <p className="text-sm text-muted-foreground mt-3 text-center text-pretty max-w-md">
                {
                  "Infelizmente não é possível prosseguir com o financiamento com o valor da sua renda."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  type="button"
                  onClick={handleAdjustIncome}
                  className="h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-xl hover:shadow-lg transition-all duration-200"
                >
                  {"Minha renda é maior, quero ajustar"}
                </Button>
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outline"
                  className="h-11 px-6 font-semibold rounded-xl transition-all duration-200"
                >
                  Ok, fechar
                </Button>
              </div>
            </div>
          ) : showTestingApp ? (
            <div className="flex flex-col items-center justify-center py-8 animate-in fade-in zoom-in-95 duration-500">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-5">
                <CircleCheck className="w-9 h-9 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground text-center text-balance">
                {"Obrigado pelo seu interesse!"}
              </h3>
              <p className="text-sm text-muted-foreground mt-3 text-center text-pretty max-w-md">
                {
                  "Entendemos que você está apenas testando. Quando tiver certeza que quer financiar um imóvel, estaremos à disposição."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  type="button"
                  onClick={handleClose}
                  className="h-11 px-8 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                >
                  OK, FECHAR
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  className="h-11 px-6 font-semibold rounded-xl transition-all duration-200"
                >
                  Preencher novamente
                </Button>
              </div>
            </div>
          ) : submitted ? (
            <div className="flex flex-col items-center justify-center py-8 animate-in fade-in zoom-in-95 duration-500">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
                <CircleCheck className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground text-center text-balance">
                {"Formulário enviado com sucesso!"}
              </h3>
              <p className="text-sm text-muted-foreground mt-3 text-center text-pretty max-w-md">
                {
                  "Vamos analisar sua operação e entrar em contato com você. Pode levar até 15 dias úteis."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  type="button"
                  onClick={handleClose}
                  className="h-11 px-8 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                >
                  OK, FECHAR
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  className="h-11 px-6 font-semibold rounded-xl transition-all duration-200"
                >
                  Preencher novamente
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Header do dialog */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 pr-4">
                  <h2 className="text-xl font-bold text-primary text-balance">
                    {headlineText}
                  </h2>
                  <p className="text-base font-semibold text-muted-foreground mt-1">
                    {subheadlineText}
                  </p>
                </div>
                <img
                  src="https://auvpcapital.com.br/wp-content/uploads/2025/03/AUVP-CAPITAL-HORIZONTAL-AMARELA.svg"
                  alt="Investidor Sardinha"
                  className="h-12 object-contain shrink-0 hidden sm:block"
                />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Aviso de inelegibilidade no topo */}
                {isNotEligible && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-700">
                      O valor do crédito ({formatCurrency(creditValue)}) é
                      inferior ao mínimo de <strong>R$ 100.000</strong>. A
                      análise de crédito está disponível para valores acima de
                      R$ 100.000. Ajuste o valor do imóvel ou da entrada.
                    </p>
                  </div>
                )}

                {/* Campos Iniciais */}
                <div className="space-y-3">
                  <div className="relative w-full">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
                    <Input
                      id="nome"
                      placeholder="Seu nome completo"
                      value={formData.nome}
                      noRootPaddingX
                      onChange={(e) =>
                        setFormData({ ...formData, nome: e.target.value })
                      }
                      className="w-full pl-8 h-9 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                    />
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer w-fit">
                    <input
                      type="checkbox"
                      checked={isAuvpStudent}
                      onChange={(e) => setIsAuvpStudent(e.target.checked)}
                      className="w-4 h-4 rounded accent-primary"
                    />
                    <span className="text-sm text-foreground">
                      Sou aluno AUVP
                    </span>
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5 min-w-0">
                      <Label
                        htmlFor="valorImovel"
                        className="text-xs font-medium text-muted-foreground"
                      >
                        Valor do imóvel (R$)
                      </Label>
                      <div className="relative w-full">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
                        <Input
                          id="valorImovel"
                          type="number"
                          min="0"
                          value={formData.valorImovel}
                          noRootPaddingX
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              valorImovel: Number(e.target.value),
                            })
                          }
                          className={`w-full pl-8 h-9 text-sm border-border/50 focus:border-primary focus:ring-primary/20 ${isPropertyValueTooLow ? "border-red-400 focus:border-red-400" : ""}`}
                        />
                      </div>
                      {isPropertyValueTooLow && (
                        <p className="text-xs text-red-600 mt-1">
                          Valor mínimo de <strong>R$ 500.000</strong> para
                          análise de crédito.
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5 min-w-0">
                      <Label
                        htmlFor="entradaForm"
                        className="text-xs font-medium text-muted-foreground"
                      >
                        Entrada (R$)
                      </Label>
                      <div className="relative w-full">
                        <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
                        <Input
                          id="entradaForm"
                          type="number"
                          min="0"
                          value={formData.entrada}
                          noRootPaddingX
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              entrada: Number(e.target.value),
                            })
                          }
                          className="w-full pl-8 h-9 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5 min-w-0">
                      <Label
                        htmlFor="prazoForm"
                        className="text-xs font-medium text-muted-foreground"
                      >
                        Prazo (meses)
                      </Label>
                      <div className="relative w-full">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
                        <Input
                          id="prazoForm"
                          type="number"
                          min="1"
                          value={formData.prazo}
                          noRootPaddingX
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              prazo: Number(e.target.value),
                            })
                          }
                          className="w-full pl-8 h-9 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Erros de validacao */}
                {validationErrors.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300 relative">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                    <div className="pr-6">
                      <p className="text-sm font-medium text-amber-700">
                        {"Preencha os campos obrigatórios corretamente:"}
                      </p>
                      <ul className="text-sm text-amber-600 mt-1 list-disc list-inside">
                        {validationErrors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                    </div>
                    <button
                      type="button"
                      onClick={() => setValidationErrors([])}
                      className="absolute top-3 right-3 text-amber-400 hover:text-amber-600 transition-colors"
                      aria-label="Fechar mensagem"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Campos Adicionais */}
                <div className="space-y-5">
                  {/* Telefone e E-mail */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 min-w-0">
                      <Label
                        htmlFor="telefone"
                        className="text-sm font-medium text-foreground"
                      >
                        Telefone (WhatsApp)
                      </Label>
                      <div className="relative w-full">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
                        <Input
                          id="telefone"
                          placeholder="(00) 00000-0000"
                          value={formData.telefone}
                          noRootPaddingX
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              telefone: formatPhone(e.target.value),
                            })
                          }
                          className="w-full pl-9 h-11 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                          maxLength={15}
                        />
                      </div>
                    </div>

                    <div className="space-y-2 min-w-0">
                      <Label
                        htmlFor="email"
                        className="text-sm font-medium text-foreground"
                      >
                        E-mail
                      </Label>
                      <div className="relative w-full">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="Seu melhor e-mail"
                          value={formData.email}
                          noRootPaddingX
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          className="w-full pl-10 h-11 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                        />
                      </div>
                    </div>
                  </div>

                  {/* CPF e Prazo Início */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 min-w-0">
                      <Label
                        htmlFor="cpf"
                        className="text-sm font-medium text-foreground"
                      >
                        CPF
                      </Label>
                      <div className="relative w-full">
                        <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
                        <Input
                          id="cpf"
                          placeholder="000.000.000-00"
                          value={formData.cpf}
                          noRootPaddingX
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              cpf: formatCPF(e.target.value),
                            })
                          }
                          className="w-full pl-10 h-11 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                          maxLength={14}
                        />
                      </div>
                    </div>

                    <div className="space-y-2 min-w-0">
                      <Label
                        htmlFor="timeToBuy"
                        className="text-sm font-medium text-foreground"
                      >
                        {"Quando iniciar?"}
                      </Label>
                      <div className="relative w-full">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 z-10" />
                        <Select
                          value={formData.timeToBuy}
                          onValueChange={(value) =>
                            setFormData({ ...formData, timeToBuy: value })
                          }
                        >
                          <SelectTrigger
                            noRootPaddingX
                            className="w-full pl-10 h-11 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                          >
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="immediately">
                              Imediatamente
                            </SelectItem>
                            <SelectItem value="withinAMonth">
                              Em até 1 mês
                            </SelectItem>
                            <SelectItem value="withinThreeMonths">
                              Em até 3 meses
                            </SelectItem>
                            <SelectItem value="aboveThreeMonths">
                              Mais de 3 meses
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Data de Nascimento e Tipo de Imóvel */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 min-w-0">
                      <Label
                        htmlFor="dataNascimento"
                        className="text-sm font-medium text-foreground"
                      >
                        Data de nascimento
                      </Label>
                      <div className="relative w-full">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 pointer-events-none" />
                        <Input
                          id="dataNascimento"
                          type="date"
                          max={new Date().toISOString().split("T")[0]}
                          value={formData.dataNascimento}
                          noRootPaddingX
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              dataNascimento: e.target.value,
                            })
                          }
                          className="w-full pl-10 h-11 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 min-w-0">
                      <Label
                        htmlFor="tipoImovel"
                        className="text-sm font-medium text-foreground"
                      >
                        Tipo de imóvel
                      </Label>
                      <div className="relative w-full">
                        <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 z-10" />
                        <Select
                          value={formData.tipoImovel}
                          onValueChange={(value) =>
                            setFormData({ ...formData, tipoImovel: value })
                          }
                        >
                          <SelectTrigger
                            noRootPaddingX
                            className="w-full pl-10 h-11 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                          >
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="casa">Casa</SelectItem>
                            <SelectItem value="apartamento">
                              Apartamento
                            </SelectItem>
                            <SelectItem value="sala">Sala Comercial</SelectItem>
                            <SelectItem value="terreno">Terreno</SelectItem>
                            <SelectItem value="galpao">Galpão</SelectItem>
                            <SelectItem value="predio">Prédio</SelectItem>
                            <SelectItem value="fabrica">Fábrica</SelectItem>
                            <SelectItem value="terreno_em_condominio">
                              Terreno em Condomínio
                            </SelectItem>
                            <SelectItem value="outros">Outros</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Renda e Perfil Profissional */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 min-w-0">
                      <Label
                        htmlFor="rendaMensal"
                        className="text-sm font-medium text-foreground"
                      >
                        Renda mensal (R$)
                      </Label>
                      <div className="relative w-full">
                        <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
                        <Input
                          id="rendaMensal"
                          type="number"
                          min="0"
                          placeholder="Ex: 15000"
                          value={formData.rendaMensal}
                          noRootPaddingX
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              rendaMensal: e.target.value,
                            })
                          }
                          className="w-full pl-9 h-11 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 min-w-0">
                      <Label
                        htmlFor="perfilProfissional"
                        className="text-sm font-medium text-foreground"
                      >
                        Perfil profissional
                      </Label>
                      <div className="relative w-full">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 z-10" />
                        <Select
                          value={formData.perfilProfissional}
                          onValueChange={(value) =>
                            setFormData({
                              ...formData,
                              perfilProfissional: value,
                            })
                          }
                        >
                          <SelectTrigger
                            noRootPaddingX
                            className="w-full pl-9 h-11 text-sm border-border/50 focus:border-primary focus:ring-primary/20"
                          >
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="assalariado">
                              Assalariado
                            </SelectItem>
                            <SelectItem value="aposentado">
                              Aposentado
                            </SelectItem>
                            <SelectItem value="pensionista">
                              Pensionista
                            </SelectItem>
                            <SelectItem value="autonomo">Autônomo</SelectItem>
                            <SelectItem value="empresario">
                              Empresário
                            </SelectItem>
                            <SelectItem value="profissional_liberal">
                              Profissional Liberal
                            </SelectItem>
                            <SelectItem value="funcionario_publico">
                              Funcionário Público
                            </SelectItem>
                            <SelectItem value="desempregado">
                              Desempregado
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-foreground">
                      {"Você tem interesse no financiamento?"}
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center space-x-3 p-3 rounded-xl border border-border/50 bg-white hover:bg-muted/40 transition-colors cursor-pointer">
                        <input
                          type="radio"
                          name="interesse"
                          value="sim"
                          checked={formData.interesseFinanciamento === "sim"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              interesseFinanciamento: e.target.value,
                            })
                          }
                          className="w-4 h-4 text-primary accent-primary"
                        />
                        <span className="text-sm flex-1 font-medium">
                          Sim, tenho.
                        </span>
                      </label>
                      <label className="flex items-center space-x-3 p-3 rounded-xl border border-border/50 bg-white hover:bg-muted/40 transition-colors cursor-pointer">
                        <input
                          type="radio"
                          name="interesse"
                          value="nao"
                          checked={formData.interesseFinanciamento === "nao"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              interesseFinanciamento: e.target.value,
                            })
                          }
                          className="w-4 h-4 text-primary accent-primary"
                        />
                        <span className="text-sm flex-1 font-medium">
                          Não, apenas testando.
                        </span>
                      </label>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isNotEligible || isPropertyValueTooLow}
                    className="w-full h-12 mt-4 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                  >
                    {ctaText}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
